import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SpecialsShimmer } from '../components/shimmer';
import { useListOffersQuery } from '../store/siteContentApi';
import { buildRoute, ROUTES } from '../constants/routes';
import { Arrow } from '../components/home/Hero';
import {
  UI, EASE, wrap, pheroWarm, pheroIn, hXl, hS, lead, eyebrowAmber, sec, secS,
  cardHov, chipOffer, offerPct, offerPctNum, offerPctLabel, kicker, go,
  price as priceSx, mrp, empty, num,
} from '../styles/tl5';

// ─── Helpers ────────────────────────────────────────────────
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Mirrors admin-side `computeFinal`. Kept duplicated (not a shared util)
// because the two apps ship separately and the math is trivial.
const computeFinal = (o) => {
  const base = Number(o?.original_price) || 0;
  const v = Number(o?.offer_value) || 0;
  if (o?.offer_type === 'flat') return Math.max(0, base - v);
  return Math.max(0, Math.round(base * (1 - v / 100)));
};

// The two halves of the square discount tile. A flat-rupee offer has no
// percentage to show, so it gets the amount and "OFF" instead.
const pctParts = (o) => {
  if (!o) return null;
  if (o.offer_type === 'flat') {
    const v = Number(o.offer_value) || 0;
    if (!v) return null;
    return [`₹${v.toLocaleString('en-IN')}`, 'OFF'];
  }
  const v = Number(o.offer_value) || 0;
  return v ? [`${v}%`, 'OFF'] : null;
};

// Humanise the validity end — "Ends 30 Apr" (or empty if open-ended).
const formatValidity = (iso) => {
  if (!iso) return 'Limited time';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Limited time';
  return `Ends ${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;
};

const ClockIcon = (
  <Box
    component="svg"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    sx={{ width: 13, height: 13, minWidth: 13, display: 'block' }}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Box>
);

const Specials = () => {
  const navigate = useNavigate();
  const { data: offers = [], isLoading } = useListOffersQuery();
  const [activeCampaign, setActiveCampaign] = useState(null); // null = "All"

  // Derive campaign pills from the active offers. An "All" option sits at
  // the front; remaining pills are whatever unique, non-empty campaign
  // tags admins have assigned.
  const campaigns = useMemo(() => {
    const seen = new Map();
    for (const o of offers) {
      const key = (o.campaign || '').trim();
      if (!key) continue;
      if (!seen.has(key)) seen.set(key, key);
    }
    return ['All', ...Array.from(seen.keys())];
  }, [offers]);

  const visibleOffers = useMemo(() => {
    if (!activeCampaign || activeCampaign === 'All') return offers;
    return offers.filter((o) => (o.campaign || '').trim() === activeCampaign);
  }, [offers, activeCampaign]);

  const heroSavings = useMemo(() => {
    if (!visibleOffers.length) return 0;
    return visibleOffers.reduce((m, o) => {
      if (o.offer_type !== 'percent') return m;
      return Math.max(m, Number(o.offer_value) || 0);
    }, 0);
  }, [visibleOffers]);

  const openOffer = (o) => navigate(buildRoute(ROUTES.TEST_DETAIL, { id: o.test_id }));

  return (
    <Box>
      {/* ═══ HERO ═══ */}
      <Box component="section" sx={pheroWarm}>
        <Box sx={{ ...wrap, ...pheroIn }}>
          <Box component="span" sx={eyebrowAmber}>Limited time</Box>

          <Typography component="h1" sx={hXl}>Special Offers</Typography>

          <Typography sx={{ ...lead, maxWidth: 640 }}>
            Every offer includes free home collection, and every report is verified
            at our NABL-accredited labs.
          </Typography>

          {/* Campaign filter — only when admins have tagged more than one. */}
          {campaigns.length > 1 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '6px' }}>
              {campaigns.map((c) => {
                const isActive = (c === 'All' && !activeCampaign) || activeCampaign === c;
                return (
                  <Box
                    key={c}
                    component="button"
                    type="button"
                    onClick={() => setActiveCampaign(c === 'All' ? null : c)}
                    aria-pressed={isActive}
                    sx={{
                      minHeight: 40,
                      px: '16px',
                      borderRadius: 999,
                      border: '1.5px solid',
                      borderColor: isActive ? 'var(--tl-g9)' : 'var(--tl-line-2)',
                      bgcolor: isActive ? 'var(--tl-g9)' : 'transparent',
                      color: isActive ? '#fff' : 'var(--tl-g9)',
                      fontFamily: UI,
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: `all .25s ${EASE}`,
                      '&:hover': { borderColor: 'var(--tl-g9)' },
                    }}
                  >
                    {c}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* ═══ GRID ═══ */}
      <Box component="section" sx={sec}>
        <Box sx={wrap}>
          {!isLoading && visibleOffers.length > 0 && (
            <Typography sx={{ ...num, fontFamily: UI, fontSize: 14.5, color: 'var(--tl-muted)', mb: '18px' }}>
              {visibleOffers.length} offer{visibleOffers.length === 1 ? '' : 's'}
              {heroSavings > 0 && ` · save up to ${heroSavings}%`}
            </Typography>
          )}

          {isLoading && <SpecialsShimmer count={6} />}

          {!isLoading && visibleOffers.length === 0 && (
            <Box sx={empty}>
              <Typography sx={{ fontSize: 34, lineHeight: 1 }}>🏷️</Typography>
              <Typography component="h2" sx={hS}>No offers running right now</Typography>
              <Typography sx={{ fontFamily: UI, fontSize: 15, color: 'var(--tl-muted)', maxWidth: 380 }}>
                New specials go live here as soon as we launch them — check back soon.
              </Typography>
            </Box>
          )}

          {!isLoading && visibleOffers.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(330px, 100%), 1fr))',
                gap: '20px',
              }}
            >
              {visibleOffers.map((o, i) => {
                const final = computeFinal(o);
                const hasOriginal = Number(o.original_price) > 0 && final < Number(o.original_price);
                const pct = pctParts(o);
                return (
                  <Box
                    key={o._id}
                    onClick={() => openOffer(o)}
                    sx={{
                      ...cardHov,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                      p: '26px',
                      animation: `tl-stgin .55s ${EASE} both`,
                      animationDelay: `${Math.min(i, 5) * 60}ms`,
                      '&:hover .tl-pct': { transform: 'scale(1.08)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '14px' }}>
                      <Box component="span" sx={chipOffer}>
                        {ClockIcon}
                        {formatValidity(o.valid_until)}
                      </Box>
                      {pct && (
                        <Box className="tl-pct" sx={offerPct}>
                          <Box component="b" sx={offerPctNum}>{pct[0]}</Box>
                          <Box component="span" sx={offerPctLabel}>{pct[1]}</Box>
                        </Box>
                      )}
                    </Box>

                    <Box>
                      {/* EDOS offers carry no test/package flag, so this slot
                          shows the campaign the admin tagged instead. */}
                      <Box sx={{ ...kicker, mb: '6px' }}>{o.campaign?.trim() || 'Offer'}</Box>
                      <Typography component="h2" sx={hS}>{o.title || o.test_name}</Typography>
                    </Box>

                    {o.description && (
                      <Typography sx={{ fontFamily: UI, fontSize: 14, lineHeight: 1.5, color: 'var(--tl-muted)' }}>
                        {o.description}
                      </Typography>
                    )}

                    <Box sx={{ mt: 'auto', pt: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0 }}>
                        <Box component="span" sx={{ ...priceSx, fontSize: 28 }}>{fmtINR(final)}</Box>
                        {hasOriginal && <Box component="span" sx={mrp}>{fmtINR(o.original_price)}</Box>}
                      </Box>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 14.5, fontWeight: 800, color: 'var(--tl-g9)' }}>
                          View Details
                        </Box>
                        <Box className="tl-go" sx={{ ...go, width: 34, height: 34 }}>
                          <Arrow size={15} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Breathing room before the footer when the grid is short. */}
      <Box sx={secS} />
    </Box>
  );
};

export default Specials;
